import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";

/* ------------------------------------------------------------------ */
/*  Local style snippets — pulled verbatim from the original HTML       */
/* ------------------------------------------------------------------ */

const trustChip: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "14px 20px",
  background: "var(--bg-white)",
  border: "1px solid rgba(15,23,42,0.08)",
  borderRadius: 10,
};
const trustChipDot: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "var(--teal)",
  flexShrink: 0,
};
const trustChipLabel: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontWeight: 600,
  fontSize: 15,
};
const cardIconBox: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  background: "rgba(31,95,91,0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 20,
};
const rowIconBox: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  background: "rgba(31,95,91,0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  marginTop: 2,
};
const rowGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gap: 20,
  alignItems: "start",
  padding: "28px 0",
};
const rowGridDivider: React.CSSProperties = {
  ...rowGrid,
  borderBottom: "1px solid rgba(15,23,42,0.08)",
};
const rowTitle: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontWeight: 700,
  fontSize: 16,
  marginBottom: 6,
};
const rowBody: React.CSSProperties = {
  color: "var(--text-muted)",
  lineHeight: 1.7,
  margin: 0,
};

/* ------------------------------------------------------------------ */
/*  Tiny presentation helpers                                          */
/* ------------------------------------------------------------------ */

function PainCard({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="pain-card">
      <div style={cardIconBox}>{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function IconRow({
  icon,
  title,
  body,
  divider = true,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  divider?: boolean;
}) {
  return (
    <div style={divider ? rowGridDivider : rowGrid}>
      <div style={rowIconBox}>{icon}</div>
      <div>
        <div style={rowTitle}>{title}</div>
        <p style={rowBody}>{body}</p>
      </div>
    </div>
  );
}

const teal = "var(--teal)";

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function TrustPage() {
  return (
    <Layout>
      {/* Hero */}
      <section
        className="trust-hero"
        style={{
          padding: "56px 0 72px",
          background: "var(--teal)",
          position: "relative",
          overflowX: "hidden",
        }}
        aria-labelledby="trust-title"
      >
        <div className="container section-reveal">
          <div style={{ maxWidth: 620 }}>
            <span
              style={{
                display: "block",
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--orange)",
                marginBottom: 16,
              }}
            >
              TRUST &amp; SECURITY
            </span>
            <h1
              id="trust-title"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(32px,4vw,48px)",
                fontWeight: 700,
                color: "var(--text-on-dark)",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                marginBottom: 20,
              }}
            >
              Built for{" "}
              <em
                className="trust-hero__accent"
                style={{ fontStyle: "italic", color: "var(--orange)" }}
              >
                operational trust.
              </em>
            </h1>
            <p
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 16,
                color: "var(--text-on-dark)",
                lineHeight: 1.65,
                maxWidth: 480,
              }}
            >
              Maltora is designed to help breweries run critical workflows with
              more clarity, control, and confidence: from ordering and inventory
              to payments and execution.
            </p>
          </div>
        </div>
      </section>

      {/* Why customers trust Maltora */}
      <section className="section" aria-labelledby="why-trust-title">
        <div className="container section-reveal">
          <div className="trust-split">
            <div>
              <span className="overline">WHY CUSTOMERS TRUST MALTORA</span>
              <h2
                id="why-trust-title"
                className="trust-intro-title"
                style={{ marginTop: 12, marginBottom: 20 }}
              >
                Maltora is built for real operational use, not lightweight
                experimentation.
              </h2>
              <p
                style={{
                  color: "var(--text-muted)",
                  lineHeight: 1.7,
                  marginBottom: 16,
                }}
              >
                We understand that for breweries and beverage businesses,
                operational software sits close to revenue, customer
                relationships, inventory movement, and payment visibility. That
                means trust is not optional. It is part of the product.
              </p>
              <p
                style={{
                  color: "var(--text-muted)",
                  lineHeight: 1.7,
                  marginBottom: 28,
                }}
              >
                That trust comes from three things:
              </p>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {["Clear workflows", "Secure access", "Reliable support"].map(
                  (label) => (
                    <div style={trustChip} key={label}>
                      <span style={trustChipDot}></span>
                      <span style={trustChipLabel}>{label}</span>
                    </div>
                  )
                )}
              </div>
            </div>

            <div className="trust-visual-grid">
              <div
                style={{
                  background: "var(--bg-white)",
                  border: "1px solid rgba(15,23,42,0.08)",
                  borderRadius: 12,
                  padding: 28,
                  gridColumn: "1/-1",
                }}
              >
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    fontFamily: "var(--font-display)",
                    color: "var(--teal)",
                    lineHeight: 1,
                  }}
                >
                  Operational
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--text-muted)",
                    marginTop: 6,
                    lineHeight: 1.5,
                  }}
                >
                  Software built for real workflows, not demos or pilots
                </div>
              </div>
              <div
                style={{
                  background: "var(--navy)",
                  borderRadius: 12,
                  padding: 28,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "rgba(247,244,239,0.6)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 8,
                  }}
                >
                  Revenue-adjacent
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "rgba(247,244,239,0.8)",
                    lineHeight: 1.5,
                  }}
                >
                  Close to orders, payments &amp; accounts
                </div>
              </div>
              <div
                style={{
                  background: "var(--bg-white)",
                  border: "1px solid rgba(15,23,42,0.08)",
                  borderRadius: 12,
                  padding: 28,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--teal)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 8,
                  }}
                >
                  Trust-first
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--text-muted)",
                    lineHeight: 1.5,
                  }}
                >
                  Clarity &amp; control at every step
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & access */}
      <section className="section white" aria-labelledby="security-title">
        <div className="container section-reveal">
          <div className="section-header" style={{ marginBottom: 48 }}>
            <span className="overline">SECURITY &amp; ACCESS</span>
            <h2
              id="security-title"
              className="trust-section-title"
              style={{ marginTop: 8, maxWidth: 480 }}
            >
              Controlled access.
              <br />
              Protected operations.
            </h2>
          </div>

          <div className="trust-cards-3">
            <PainCard
              title="Controlled access by role"
              body="Maltora is built around role-based workflows, so different users only see and manage what is relevant to their role."
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={teal}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              }
            />
            <PainCard
              title="Secure account access"
              body="Customer, seller, and internal users access the platform through authenticated login flows designed to protect operational access."
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={teal}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              }
            />
            <PainCard
              title="Structured permissions"
              body="The platform is designed to support controlled access across customers, admin teams, and sales representatives, reducing confusion and protecting operational visibility."
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={teal}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              }
            />
          </div>
        </div>
      </section>

      {/* Data handling */}
      <section className="section" aria-labelledby="data-title">
        <div className="container section-reveal">
          <div className="trust-split-narrow">
            <div>
              <span className="overline">DATA HANDLING</span>
              <h2
                id="data-title"
                className="trust-section-title"
                style={{ marginTop: 8, marginBottom: 16 }}
              >
                One clear source of truth.
              </h2>
              <p style={{ color: "var(--text-muted)", lineHeight: 1.7 }}>
                Maltora helps centralize key workflow data, replacing fragmented
                channels with a single, clear operational picture.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <IconRow
                title="Operational data in one system"
                body="Maltora helps centralize key workflow data such as orders, inventory, customer details, shipment progress, invoices, and payment status."
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={teal}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <ellipse cx="12" cy="5" rx="9" ry="3" />
                    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                  </svg>
                }
              />
              <IconRow
                title="Better visibility, less fragmentation"
                body="Instead of spreading operational activity across disconnected channels, Maltora creates one clearer source of truth for day-to-day distribution workflows."
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={teal}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                }
              />
              <IconRow
                divider={false}
                title="Respect for business-critical information"
                body="We recognize that operational and account data is sensitive. Maltora is designed to handle this information with care and clarity."
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={teal}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Reliability & support */}
      <section className="section white" aria-labelledby="reliability-title">
        <div className="container section-reveal">
          <div className="section-header" style={{ marginBottom: 48 }}>
            <span className="overline">RELIABILITY &amp; SUPPORT</span>
            <h2
              id="reliability-title"
              className="trust-section-title"
              style={{ marginTop: 8, maxWidth: 480 }}
            >
              Built around continuity.
              <br />
              Backed by real support.
            </h2>
          </div>

          <div className="trust-cards-3">
            <PainCard
              title="Built around operational continuity"
              body="Maltora is intended to support critical day-to-day processes, which means reliability, clarity, and usability matter at every step."
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={teal}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              }
            />
            <PainCard
              title="Support when it matters"
              body="When teams rely on software for live operations, they need responsive help and clear escalation paths. Maltora is built with that reality in mind."
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={teal}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              }
            />
            <PainCard
              title="A platform that grows with the business"
              body="As order volume, accounts, and complexity increase, Maltora is designed to bring more structure, not more operational chaos."
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={teal}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              }
            />
          </div>
        </div>
      </section>

      {/* Legal & compliance */}
      <section className="section" aria-labelledby="legal-title">
        <div className="container section-reveal">
          <div className="trust-split-narrow">
            <div>
              <span className="overline">LEGAL &amp; COMPLIANCE</span>
              <h2
                id="legal-title"
                className="trust-section-title"
                style={{ marginTop: 8, marginBottom: 16 }}
              >
                Transparent operating boundaries.
              </h2>
              <p style={{ color: "var(--text-muted)", lineHeight: 1.7 }}>
                We believe trust comes from being clear about what the product
                does and what it does not claim to do.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <IconRow
                title="Clear commercial scope"
                body="Maltora is a beverage operations platform. It supports operational workflows, but it does not replace internal legal, financial, or regulatory review processes."
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={teal}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                }
              />
              <IconRow
                title="Customer responsibility where required"
                body="Customers remain responsible for reviewing their own business, tax, excise, invoice, and regulatory obligations as applicable."
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={teal}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                }
              />
              <IconRow
                divider={false}
                title="No hidden caveats"
                body="Our terms of service and privacy policy are written in plain language. We do not bury limitations in fine print. If something is out of scope, we say so upfront."
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={teal}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section
        className="cta-band grain"
        id="cta"
        aria-labelledby="trust-cta-title"
      >
        <div className="container">
          <div className="cta-band__content section-reveal">
            <span className="overline overline--light">GET IN TOUCH</span>
            <h2 id="trust-cta-title">
              Have questions about security,
              <br />
              workflows, or rollout?
            </h2>
            <p>
              We&apos;re happy to walk you through how Maltora fits into your
              operations.
            </p>
            <div className="btn-row">
              <Link
                to="/contact"
                state={{ fromApp: true }}
                className="btn btn-accent btn-lg"
              >
                Get in touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
